import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Forwarded to Sentry beacon once VITE_SENTRY_DSN is wired in F2c.
    // eslint-disable-next-line no-console
    console.error("react_error_boundary", error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div role="alert" style={{ padding: 24 }}>
        <h2>Something went wrong.</h2>
        <pre style={{ whiteSpace: "pre-wrap", color: "#b00" }}>{error.message}</pre>
        <button type="button" onClick={this.reset}>
          Try again
        </button>
      </div>
    );
  }
}
